// ./src/hooks/useEventTimeSlots.ts
//
// Loads the time slots (occurrences / sub-events) for one event, chronological.
// Used by the event schedule page. `reload` refetches after a mutation.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { EventTimeSlotService, EventTimeSlot } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseEventTimeSlotsReturn {
  slots: EventTimeSlot[];
  loading: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useEventTimeSlots = (eventId?: string | null): UseEventTimeSlotsReturn => {
  const [slots, setSlots] = useState<EventTimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setSlots([]);
      setIsLoaded(true);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await EventTimeSlotService.list({ eventId: [eventId] });

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setSlots(result.data as EventTimeSlot[]);
    } else {
      setError(result);
      setSlots([]);
    }
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    load();
  }, [load]);

  return { slots, loading, isLoaded, error, reload: load };
};
